/**
 * WT1 - the capture screen's words, kept pure so they are tested apart from the screen.
 *
 * ⭐⭐ The number is ALWAYS on screen and never behind a control (spec section 5): his ruling, "defer prose, never a
 *   number, and a control must name its contents". A hidden upload queue is the "Show all 7" defect with worse
 *   consequences. ⛔ Unreadable is unknown, never empty: a photo that could not upload says so, in the server's
 *   own words.
 */
import { WAITING_FOR_SIGNAL, type CaptureRecord, type TourCounts, type TourRecord } from './tourQueue';

const photos = (n: number): string => `${n} ${n === 1 ? 'photo' : 'photos'}`;

/** `23 photos · 6 not yet uploaded` - the line pinned to the top of the capture screen. */
export const counterText = ({ total, outstanding, failed }: TourCounts): string => {
  if (total === 0) return 'No photos yet';
  const parts = [photos(total), outstanding ? `${outstanding} not yet uploaded` : 'all uploaded'];
  if (failed) parts.push(`${failed} could not upload`);
  return parts.join(' · ');
};

/**
 * The count from the list the screen ALREADY read - the queue's own rule. ⭐ Never a second read: every record carries
 * its photo's bytes until upload, so a second read of 40 waiting photos is another ~25 MB, every 20 s, on the phone.
 */
export const countsOf = (captures: CaptureRecord[]): TourCounts => {
  const uploaded = captures.filter((c) => c.state === 'uploaded').length;
  return {
    total: captures.length,
    uploaded,
    outstanding: captures.length - uploaded,
    failed: captures.filter((c) => c.state === 'failed').length,
  };
};

/**
 * What this phone holds that the server does not have yet, besides photos: unsent notes or tag set (or a sync the
 * server refused), and each photo edit not yet sent. ⛔ After Finish only the capture screen drains, and a finished
 * tour is not reopened there - so anything counted here would be stranded, and the report would print "none".
 */
export const unsyncedEdits = (tour: TourRecord, captures: CaptureRecord[]): number =>
  (tour.notesDirty || tour.tagSetDirty || tour.syncError ? 1 : 0) + captures.filter((c) => c.metaDirty).length;

/**
 * Why "Finish tour" cannot finish yet - or null when it can. The server refuses the same way (409) for photos; the
 * phone alone knows about a photo still in its panel and about edits not yet sent.
 */
export const finishRefusal = (
  { outstanding, failed }: TourCounts,
  { pendingPhoto = false, unsynced = 0 }: { pendingPhoto?: boolean; unsynced?: number } = {},
): string | null => {
  if (pendingPhoto) return 'Save or discard the photo you just took first.';
  if (!outstanding) return unsynced ? 'Your notes or edits have not saved yet - finish the tour when they have.' : null;
  const head =
    outstanding === 1
      ? '1 photo has not uploaded yet - finish the tour when it has.'
      : `${outstanding} photos have not uploaded yet - finish the tour when they have.`;
  return failed ? `${head} ${failed} could not upload; its reason is on the photo.` : head;
};

export type CaptureTone = 'done' | 'busy' | 'waiting' | 'failed';

/**
 * Where one photo is, as its tile says it. `offline`: the last drain found no signal - it stops at its first
 * unreachable request, so it stamps at most one photo, but every waiting photo is waiting for the same reason.
 */
export const captureStatus = (
  c: CaptureRecord,
  { offline = false }: { offline?: boolean } = {},
): { tone: CaptureTone; text: string } => {
  switch (c.state) {
    case 'uploaded':
      return { tone: 'done', text: 'Uploaded' };
    case 'uploading':
      return { tone: 'busy', text: 'Uploading' };
    case 'failed':
      return {
        tone: 'failed',
        text: c.lastError ? `Could not upload: ${c.lastError}` : 'Could not upload - the server gave no reason.',
      };
    default:
      if (offline || c.lastError === WAITING_FOR_SIGNAL) return { tone: 'waiting', text: WAITING_FOR_SIGNAL };
      if (!c.lastError) return { tone: 'waiting', text: 'Not yet uploaded' };
      return { tone: 'waiting', text: `Not yet uploaded - ${c.lastError}` };
  }
};
